import { SELECTED_TRANSACTIONS_STORAGE_KEY } from './selectedTransactions.utils';

import insane from 'insane';

/* eslint-disable: no-useless-escape */
export const PREFIX_CHAT_TRIGGERS = 'cache:messageTriggers:';

export const pick = (obj: any, keys: string[]) => {
  return keys.reduce((acc, key) => ({
    ...acc,
    [key]: obj[key],
  }), {});
};

const MAX_DOMAIN_LENGTH = 49;

// https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
export const isEmailValid = (email: string | any) => {
  if (typeof email !== 'string') return false;
  // eslint-disable-next-line no-useless-escape
  const emailReg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!emailReg.test(email)) return false;

  const domain = email.split('@')[1];

  return !!(domain && domain.length <= MAX_DOMAIN_LENGTH);
};

export const isPhoneNumber = (phone?: string) => {
  if (!phone) return false;
  // Regex pattern for common phone number formats
  const pattern = /^(\+?\d{1,3}[-.\s]?)?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/;
  return pattern.test(phone);
};

export const isPhoneValid = (phone: string) => { // TODO
  return phone.length > 7 && !isNaN(+phone);
};

// TODO: [Sportybet]
// export const fixPhone = (phone = '') => {
//   const country = (store.state as any)['user'].country;
//   if (
//     phone &&
//     country === 'ke' &&
//     phone && !/^0/.test(phone)
//   ) {
//     const temp = `0${phone}`;
//     return temp.length > 18 ? temp.slice(0, 18) : temp;
//   } else if (phone && country === 'zm') {
//     if (!/^0/.test(phone) && phone.length === 9) {
//       return `0${phone}`;
//     }
//   }
//   return phone;
// };

export const isPswdValid = (pswd: string) => {
  return !!pswd.length;
};

export const formatMoney = (num: number | string) => {
  if (typeof num === 'string') {
    num = +num;
  }
  return (num / 10000).toFixed(2);
};

export const stringLengthWithoutSpace = (str: string) => {
  return str.trim().replace(/ /g, '').length;
};

export const combinedDescription = (description: string, issueMsg = '') => {
  return issueMsg ? `My Issue: ${issueMsg} + My description: ${description}` : description;
};

export const previewHelper = (htmlBodyString: string) => {
  const div = document.createElement('div');
  div.innerHTML = htmlBodyString;
  const text = div.textContent || div.innerText || '';
  return {
    html: htmlBodyString,
    isPreview: text.length > 75,
    text,
  };
};

export function round (num: number, decimals: number) {
  return Number(Math.round(Number(`${num}e${decimals}`)) + `e-${decimals}`);
}

export function waitFor (milliSeconds: number) {
  return new Promise(resolve => setTimeout(resolve, milliSeconds));
}

export function identifyUrls (text: string) {
  // eslint-disable-next-line no-useless-escape
  const urlRegex = /(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
  return text.replace(urlRegex, function (url) {
    return '<a class="pl-chat-url" href="' + url + '" target="_blank">' + url + '</a>';
  });
}

export function singleton<T extends () => unknown>(factory: T) {
  let val: unknown | null = null;
  const getter = () => (val || (val = factory()));
  return getter as T;
}

/**
 * Replaces placeholders in a text with values from a provided data object.
 * If a key is not found in the data object, it uses the corresponding value from the defaults object.
 * Supports nested keys.
 *
 * @param text - The text containing placeholders in the format {{key}}.
 * @param data - The data object used to replace placeholders.
 * @param defaults - The object providing default values for placeholders if not found in the data object.
 * @returns A new string with the placeholders replaced by corresponding values from the data object, or from defaults if data is missing.
 */
export function replacePlaceholders<T extends Record<string, any>, D extends Record<string, any>> (
  text: string,
  data: T,
  defaults?: D
): string {
  // Regex pattern to match placeholders like {{key}}
  const placeholderPattern = /\{\{\s*([\w.]+)\s*\}\}/g;

  return text.replace(placeholderPattern, (match, key) => {
    const keys = key.split('.');
    let value: any = data;
    let defaultValue: any = defaults || {};

    // Traverse through the nested keys to find the value in the data object
    for (let i = 0; i < keys.length; i++) {
      value = value?.[keys[i]];
      defaultValue = defaultValue?.[keys[i]];

      // If both value and defaultValue are undefined, break early and return empty string
      if (value === undefined && defaultValue === undefined) {
        return '';
      }
    }

    // If the value exists, return it; otherwise, use the default or empty string
    return value || (defaultValue !== undefined ? defaultValue : '');
  });
}

export function notEmpty<TValue> (value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

// Deep clone with structured clone if available, otherwise use JSON serialization
export function deepClone<T> (value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}

export function removeLimitedData () {
  Object.keys(localStorage).map((key) => {
    if (key.startsWith(PREFIX_CHAT_TRIGGERS) || key === END_CHAT_FOR_RESUME_AI_SETUP || key === SELECTED_TRANSACTIONS_STORAGE_KEY) {
      // We don't want to remove CHAT TRIGGERS from LS because this data is used for resumable chat to show pending trigger message, we also don't want to remove selected transactions data from LS because it is used for resumable chat to get selected transactions data tags, and also we don't want to remove end_chat_for_resume_ai_setup from LS because it is used to track sessionIds for resume ai which are explicitly ended by the user(we don't want the user to see those conversations again)
      // Refer - https://pleased.atlassian.net/browse/PLS-3751
      return;
    }
    localStorage.removeItem(key);
  });
}

export function localStorageRemoveChatTriggersKeys () {
  Object.keys(localStorage).map((key) => {
    if (key.startsWith(PREFIX_CHAT_TRIGGERS)) {
      localStorage.removeItem(key);
    }
  });
}

export function capitalizeFirstLetters (str: string) {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const END_CHAT_FOR_RESUME_AI_SETUP = 'cache:end_chat_for_resume_ai_setup';

export function sanitizeHTML (input: string): string {
  return insane(input, {
    allowedTags: ['a', 'b', 'i', 'em', 'strong', 'p', 'ul', 'ol', 'li', 'br'],
    allowedAttributes: {
      a: ['href', 'title', 'target'],
    },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedSchemesAppliedToAttributes: ['href'],
  });
}
